import type { IRequestWrap } from '@/server/IServer';
import * as SentryBrowser from '@sentry/browser';
import * as SentryNode from '@sentry/node';

import type { IRequesterError } from '../../RequestService/IRequester';

export type { SeverityLevel as EN_SENTRY_LEVEL } from '@sentry/browser';

export interface ISentryMessagesInfo {
  /* 에러 종류를 태그로 구분할 용도로 남기는 값. 프로젝트 단위로 기록하는 것이 좋을 듯 하다. */
  tag?: { name: string; value: string };
  /* 에러가 발생했을 때 처리할 수 있는 1차 담당자를 기록 */
  user?: SentryBrowser.User;
  /* 그 외 기타 남기고 싶은 값 무엇이든지 남길 수 있도록 만든 용도 */
  extraMessage?: { [key: string]: any };
  /* 에러의 심각성 */
  level?: SentryBrowser.SeverityLevel;
}

const sentry = process.browser ? SentryBrowser : SentryNode;

/**
 * [FES] Service Pages: https://yanolja.atlassian.net/wiki/spaces/FES/pages/103744562/FES+Service+Pages
 * */
const webview_supported_page_paths = [
  /^\/member/,
  /^\/mybenefit/,
  /^\/notice/,
  /^\/faq/,
  /^\/event/,
  /^\/train/,
  /^\/flights/,
  /^\/live-commerce/,
  /^\/visitorlogin/,
  /^\/yanolja-coin/,
  /^\/promotion\//,
  /^\/leisure\/story\//,
  /^\/magazine\//, // 매거진 상세
  /^\/express-bus\/offices/,
  /^\/phone-re-certificate/,
  /^[\w\/-]+\/vendor$/,
  /\/\w+\/qrcode$/,
  /^\/mypage\/coupon/, // 쿠폰함, 쿠폰 상세
  /^\/raffle-deal\//,
  /^\/ci-cert/,
  /^\/mypage\/cs/,
  /^\/mypage\/nol-card-settings/,
  /^\/reserves\/\d+$/,
];

/**
 * 웹뷰 미지원 페이지를 딥링크로 접근시, 앱으로 리다이렉트 되는 방어 로직이 적용된 페이지들
 * 센트리에서 웹뷰 미지원 페이지가 발견이 되면 조치 후, 해당 페이지 경로를 아래 배열에 추가해준다.
 * */
const redirect_to_app_applied_page_paths = [
  /^\/$/,
  /^\/mypage$/,
  /^\/search/,
  /^\/magazine$/,
  /^\/wishlist/,
  /^\/around/,
  /^\/login/,
  /^\/emaillogin/,
  /^\/exhibition/, // 기획전 리스트, 상세
];

const file_extensions_to_ignore = [/.ico$/, /.png$/];

export const getSentryRelease = (mode: string, version: string): string =>
  `sunny-web${mode !== 'production' ? `-${mode}` : ''}@${version}`;

/**
 * 아무 객체나 센트리로 전파한다. `Error` 객체인지 판별하지 않는다.
 * @param err
 * @param message 센트리에서 사용할 오류 제목.
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const captureError = (err: any, message: string): void => {
  const error = new Error(message);
  /**
   * console.error(err, response);
   * CloudWatch 에서 JSON 형태로 되어있는 response 가 error 와 묶여 필터링이 불가능해 기존코드에서 두라인으로 분리 했다
   * response 는 항상 남을 수 있도록 captureRequestErrorResponse 가 아닌 createActionRightHandler 로 이동
   */
  console.error(error);
  if (!process.browser) {
    console.log(JSON.stringify(err));
  }
  sentry.withScope((scope) => {
    scope.setExtra('data', err);
    sentry.captureException(error);
  });
};

/**
 * API의 응답이 오류인 경우 센트리로 전파한다.
 * @param response `IRequesterError`
 * @param message 센트리에서 사용할 오류 제목.
 */
export const captureRequestErrorResponse = (
  response: Omit<IRequesterError, 'success'> & { success: boolean },
  message: string,
): void => {
  if (process.browser || response.success) {
    return;
  }

  const error = new Error(message);
  /**
   * console.error(err, response);
   * CloudWatch 에서 JSON 형태로 되어있는 response 가 error 와 묶여 필터링이 불가능해 기존코드에서 두라인으로 분리 했다
   * response 는 항상 남을 수 있도록 captureRequestErrorResponse 가 아닌 createActionRightHandler 로 이동
   */
  console.error(error);

  sentry.withScope((scope: SentryNode.Scope | SentryBrowser.Scope) => {
    scope.setExtra('errorType', response.errorType);
    scope.setExtra('statusCode', response.statusCode);
    scope.setExtra('config', response.config);
    response.data && scope.setExtra('data', response.data);
    sentry.captureException(new Error(message));
  });
};

/**
 * 일반 코드 블록에서 try-catch의 catch 에 사용할 수 있는 함수.
 * 에러 객체와 센트리에 남길 정보를 매개변수로 넘긴다. 대신, info를 빈 객체로 넘기는 것은 비권장
 * @param e 에러 객체
 * @param info 센트리에 남길 추가 정보. 가급적 상세한 것이 좋다
 */
export const captureException = (e: Error, info: ISentryMessagesInfo = {}): void => {
  sentry.withScope((scope: SentryNode.Scope | SentryBrowser.Scope) => {
    info.tag && scope.setTag(info.tag.name, info.tag.value);
    info.extraMessage && scope.setExtras(info.extraMessage);
    /* level 를 추가하지 않더라도 Sentry의 default level은 Error */
    info.level && scope.setLevel(info.level);
    info.user && scope.setUser(info.user);
    sentry.captureException(e);
  });
  console.error(e);
};

/**
 * 브라우저에서 이벤트 핸들러에서 발생한 에러를 센트리에 올릴 수 있도록 해주는 메서드
 * 이벤트 핸들러를 이 메서드로 Wrapping한 코드만 에러 추적으로 센트리에 올릴 수 있다.
 * React는 이벤트 핸들러, 프로미스 등의 에러를 자동으로 캐치할 수 있는 기능을 제공하지 않기에
 * 개발자가 직접 에러에 대한 로그를 심고 싶은 부분에 Sentry 관련 메서드를 넣어주어야 한다.
 *
 * @param fn 에러 추적을 하고 싶은 이벤트 핸들러
 * @param info 센트리에 올릴 에러에 대한 추가 정보 (설명 등)
 *
 * @example
 * const sentryInfo = {
 *  tag: { name: 'page', value: 'login' },
 *  user: { username: 'Munkyu Yang', email: 'munkyu.yang_at_yanolja.com' },
 *  extraMessage: {
 *    caution: 'SDM에 디펜던시가 있으니 문서를 참고할 것',
 *    message: '해당 에러는 Critical Issue 이므로 발생 즉시 해결해야 함'
 *  }
 * }
 *
 * const onLogin = () => {
 *  // Login...
 * }
 *
 * <button onClick={trySentryCapture(onLogin, sentryInfo)}>Login</button>
 *
 * // 센트리에 부가적으로 남기고 싶은 값이 없을 때 (비추천)
 * <button onClick={trySentryCapture(onLogin)}>Login</button>
 *
 * // 파라미터를 넘겨서 처리하고 싶을 때
 * const onCapturedLoginHandler = e => {
 *  trySentryCapture(onLogin.bind(null, e), sentryInfo)();
 * }
 * <button onClick={onCapturedLoginHandler}>Login</button>
 *
 */
export const trySentryCapture = <T extends any[], R>(
  fn: (...args: T) => R,
  info: ISentryMessagesInfo = {},
): ((...args: T) => void) => (...args: T) => {
  try {
    fn(...args);
  } catch (e) {
    captureException(e, info);
  }
};

/**
 * @Desc  웹뷰로 제공하지 않는 페이지가 웹뷰로 띄어졌을 때 에러 throw 하는 함수
 *        웹뷰 지원, 미지원 여부는 하드코딩된 아래 배열들로 판단하고 있으므로 예외 발생시 수동으로 데이터를 변동해줘야 한다
 *        webview_supported_page_paths, redirect_to_app_applied_page_paths, file_extensions_to_ignore
 * @param {IRequestWrap} req
 * */
export const captureSentryOnUnsupportedPage = (req: IRequestWrap): void => {
  try {
    if (req.method !== 'GET') return;

    const path = req.path || '/';
    if (
      !webview_supported_page_paths.some((pagePath) => !!path.match(pagePath)) &&
      !redirect_to_app_applied_page_paths.some((pagePath) => !!path.match(pagePath)) &&
      !file_extensions_to_ignore.some((file) => !!path.match(file))
    ) {
      throw new Error('해당 페이지는 웹뷰를 지원하지 않습니다');
    }
  } catch (e) {
    captureException(e, {
      extraMessage: {
        referer: req.headers?.['referer'],
      },
    });
  }
};
